import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { useTheme } from "@mui/material/styles";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { Button, useMediaQuery } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { GATE_COLOR } from "../../../interfaces/robot/rooms/gate";
import ZoneControllerMouseState from "../../../enums/robot/zone-planner/zone-controller-mouse-state";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import {
  selectGatesData,
  selectLimitZones,
  selectSelectedRoom,
  selectWaypointsData,
} from "../../../store/room/room-selectors";
import ZonePlannerModeState from "../../../enums/robot/zone-planner/zone-planner-mode-state";
import {
  selectChargingStationDragData,
  selectMapHistory,
  selectSelectedZoneInstance,
  selectZonePlannerModeState,
  selectZonePlannerMouseState,
} from "../../../store/room/zone-planner/zone-planner-selectors";
import SideBarIconButton from "./SideBarIconButton";
import {
  CopyIcon,
  CropIcon,
  DeleteIcon,
  DeletePointIcon,
  DragIcon,
  EditMapIcon,
  EvacuationIcon,
  MapRulesIcon,
  LandmarkIcon,
  LayersListIcon,
  LimitZoneIcon,
  MapGridIcon,
  RedoIcon,
  RoomIcon,
  SendBelowIcon,
  SettingsIcon,
  UndoIcon,
  WaypointIcon,
} from "../../../utils/icons/icons";
import { MoveStationSaveStatus } from "../../../interfaces/robot/rooms/hub-messages/receive-move-station-command-message";
import { selectIsCleaningActionEnabled } from "../../../store/status/status-selectors";
import { LANDMARK_COLOR, EVACUATION_PLACE_COLOR } from "../../../interfaces/robot/rooms/landmark";
import EditMapUndoRedoAction from "../../../interfaces/robot/zone-planner/edit-map-undo-redo-action";
import { WAYPOINT_COLOR } from "../../../interfaces/robot/rooms/waypoints";
import CustomSnackbar from "../../Feedback/CustomSnackbar";
import ApiEndpointNames from "../../../enums/robot/configuration/api/api-endpoint-names";
import ApiProtocol from "../../../enums/robot/configuration/api/api-protocol";
import { getRobotApiConfig } from "../../../interfaces/robot/robot-apis/robot-api-config";
import {
  selectIsRobotConnected,
  selectRobotApiConfigs,
  selectRobotApiConfigsRestSet,
  selectRobotRswSoftwareVersion,
} from "../../../store/robot-configurations/robot-configurations-selectors";
import { isCleaningZone, isTeachAndRepeatZone } from "../../../interfaces/robot/rooms/cleaning-zone";
import ZoneCleaningNavigatorIcon from "../../../images/navigator-icons/ZoneCleaningNavigatorIcon.svg";
import { getDefaultCleaningCommandButtonSender } from "../../../interfaces/robot/buttons/command-buttons";
import {
  selectIsAdvancedModeEnabled,
  selectIsReadOnly,
  selectIsWebApp,
} from "../../../store/app-configurations/app-configurations-selectors";
import { NavigatorName } from "../../../enums/command-hub/navigator";
import MessageType from "../../../enums/robot/rsw/message-types";
import { FilterKeys } from "../../../enums/query-params/filters";
import SphereRobotTabs from "../../../enums/query-params/sphere-robot-tabs";
import CustomTabs from "../../misc/CustomTabsHorizontal";
import EditMapTools from "./EditMap/EditMapTools";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import { selectMapPutStatus } from "../../../store/room/maps/maps-selectors";
import { getDefaultChargingStationMove } from "../../../interfaces/robot/rooms/station";
import WebAppNavigationRoutes from "../../../enums/query-params/webapp-navigation-routes";
import { isVersionAGreaterThanB } from "../../../utils/version-util";

const useStyles = makeStyles()((theme) => ({
  mapControlsContainer: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    zIndex: 2,
    backgroundColor: "white",
    maxHeight: `calc(100% - ${theme.spacing(2)})`,
    overflowY: "auto",
  },
  topControlsContainer: {
    position: "absolute",
    top: theme.spacing(1),
    zIndex: 2,
    backgroundColor: "white",
    left: theme.spacing(1),
    [theme.breakpoints.up("lg")]: {
      left: "50%",
      transform: "translateX(-50%)",
    },
  },
  cleaningZoneIcon: {
    backgroundColor: "lightgreen",
    border: "1px solid green",
  },
  teachAndRepeatZoneIcon: {
    backgroundColor: "lightblue",
    border: "1px solid #118FF3",
  },
  redZoneIcon: {
    backgroundColor: "lightcoral",
    border: "1px solid red",
  },
  disabledZoneIcon: {
    backgroundColor: theme.palette.action.disabled,
    border: theme.palette.action.disabled,
    "&:before": {
      backgroundColor: theme.palette.action.disabled,
    },
    "&:after": {
      backgroundColor: theme.palette.action.disabled,
    },
  },
  ruleZoneIcon: {
    backgroundColor: "plum",
    border: "1px solid purple",
  },
  zoneSquareIcon: {
    width: "24px",
    height: "24px",
    borderRadius: "2px",
  },
  redZoneLineIconContainer: {
    height: "24px",
    width: "24px",
    alignItems: "center",
    paddingTop: 0,
    position: "relative",
  },
  redZoneLineIcon: {
    width: "2px",
    height: "24px",
    backgroundColor: "lightcoral",
    alignSelf: "center",
    transform: "translateX(11px)  rotate(45deg)",
    "&:before": {
      content: '""',
      width: "6px",
      height: "6px",
      borderRadius: "3px",
      backgroundColor: "red",
      position: "absolute",
      top: "-2px",
      left: "-2px",
    },
    "&:after": {
      content: '""',
      width: "6px",
      height: "6px",
      borderRadius: "3px",
      backgroundColor: "red",
      position: "absolute",
      bottom: "-2px",
      left: "-2px",
    },
    "&:disabled": {
      backgroundColor: theme.palette.action.disabled,
    },
  },
  gateIcon: {
    width: "24px",
    height: "4px",
    marginTop: "10px",
    backgroundColor: GATE_COLOR,
    alignSelf: "center",
    "&:after": {
      content: '""',
      width: "22px",
      height: "2px",
      position: "absolute",
      left: 0,
      top: "7px",
      backgroundColor: GATE_COLOR,
      transform: "rotate(165deg)",
    },
  },
  gateDisabled: {
    backgroundColor: theme.palette.action.disabled,
    "&:after": {
      backgroundColor: theme.palette.action.disabled,
    },
  },
  navigatorIcon: {
    height: "24px",
    width: "24px",
  },
  navigatorIconDisabled: {
    opacity: 0.5,
  },
}));

const DividerElement = () => <Divider />; // needed to prevent prop passing issues for Tabs

const ZonePlannerSideBar: React.FC<{
  layersList: { isVisible: boolean; toggleVisible: () => void };
  gridOverlay: { isVisible: boolean; toggleVisible: () => void };
  editedMapData: {
    mapData: Uint8ClampedArray;
    undoActions: EditMapUndoRedoAction[][];
    redoActions: EditMapUndoRedoAction[][];
  } | null;
  inMappingWizard?: boolean;
  initialMouseState?: ZoneControllerMouseState;
  overrideRoomId?: string;
  isTransitionRoom?: boolean;
  onEditMapUndoRedo: (undoAction?: boolean, redoAction?: boolean, resetMap?: boolean) => void;
}> = ({
  layersList,
  gridOverlay,
  editedMapData,
  inMappingWizard,
  initialMouseState,
  overrideRoomId,
  isTransitionRoom,
  onEditMapUndoRedo,
}) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setFilterParams] = useSearchParams();

  const dispatch = useSharedDispatch();
  const navigate = useNavigate();

  const {
    toggleWayPointVisibility,
    setZonePlannerMode,
    setZonePlannerMouseState,
    setSelectedLandmark,
    setSelectedZone,
    sendRobotWebsocketMessage,
    setSelectedWayPoint,
    undoRedoLastAction,
    setEditMapUpdateAction,
    resetMapPutStatus,
    patchStationDrag,
    getRobotApiConfigs,
  } = useSharedActions();

  const roomData = useSharedSelector((state) => selectSelectedRoom(state));
  const zonePlannerMouseState = useSharedSelector((state) => selectZonePlannerMouseState(state));
  const zonePlannerModeState = useSharedSelector((state) => selectZonePlannerModeState(state));
  const isAdvancedModeEnabled = useSharedSelector((state) => selectIsAdvancedModeEnabled(state));
  const isReadOnly = useSharedSelector((state) => selectIsReadOnly(state));
  const chargingStationDragData = useSharedSelector((state) => selectChargingStationDragData(state));
  const selectedZoneInstance = useSharedSelector((state) => selectSelectedZoneInstance(state));
  const robotApiConfigs = useSharedSelector((state) => selectRobotApiConfigs(state));
  const robotApiConfigsRest = useSharedSelector((state) => selectRobotApiConfigsRestSet(state));
  const isCleaningActionEnabled = useSharedSelector((state) => selectIsCleaningActionEnabled(state));
  const mapHistory = useSharedSelector((state) => selectMapHistory(state));
  const wayPointsData = useSharedSelector((state) => selectWaypointsData(state));
  const isWebApp = useSharedSelector((state) => selectIsWebApp(state));
  const isRobotConnected = useSharedSelector((state) => selectIsRobotConnected(state));
  const mapPutStatus = useSharedSelector((state) => selectMapPutStatus(state));
  const gatesData = useSharedSelector((state) => selectGatesData(state));
  const rswSoftwareVersion = useSharedSelector((state) => selectRobotRswSoftwareVersion(state));
  const limitZone = useSharedSelector((state) => selectLimitZones(state));

  const { t } = useTranslation();

  const matchesSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const resetZonePlannerWithMouse = useCallback(
    (mouseMode: ZoneControllerMouseState = ZoneControllerMouseState.Default) => {
      dispatch(setSelectedWayPoint({ pointId: null }));
      dispatch(setSelectedLandmark(null));
      dispatch(setSelectedZone(null));
      if (roomData != null && roomData.primaryStation != null) {
        dispatch(patchStationDrag({ ...getDefaultChargingStationMove(roomData.primaryStation) }));
      }
      return setZonePlannerMouseState(mouseMode);
    },
    [
      dispatch,
      patchStationDrag,
      setSelectedLandmark,
      setSelectedWayPoint,
      setSelectedZone,
      setZonePlannerMouseState,
      roomData,
    ],
  );

  const keyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "escape") {
        dispatch(resetZonePlannerWithMouse());
      }
    },
    [dispatch, resetZonePlannerWithMouse],
  );

  useEffect(() => {
    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [keyDownHandler]);

  useEffect(() => {
    if (robotApiConfigsRest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getRobotApiConfigs());
    }
  }, [dispatch, robotApiConfigsRest.getStatus]);

  useEffect(() => {
    if (
      !isAdvancedModeEnabled &&
      zonePlannerModeState !== ZonePlannerModeState.Default &&
      zonePlannerModeState !== ZonePlannerModeState.MapRules
    ) {
      dispatch(setZonePlannerMode(ZonePlannerModeState.Default));
    }
  }, [dispatch, isAdvancedModeEnabled, zonePlannerModeState]);

  useEffect(() => {
    dispatch(setZonePlannerMouseState(initialMouseState ?? ZoneControllerMouseState.Default));
  }, [dispatch, initialMouseState, zonePlannerModeState]);

  const addCleaningZoneClickHandler = () => {
    dispatch(
      resetZonePlannerWithMouse(
        zonePlannerMouseState === ZoneControllerMouseState.AddCleaningZone
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.AddCleaningZone,
      ),
    );
  };

  const addTeachAndRepeatZoneClickHandler = () => {
    dispatch(
      resetZonePlannerWithMouse(
        zonePlannerMouseState === ZoneControllerMouseState.AddTeachAndRepeatZone
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.AddTeachAndRepeatZone,
      ),
    );
  };

  const addRedZoneClickHandler = () => {
    dispatch(
      resetZonePlannerWithMouse(
        zonePlannerMouseState === ZoneControllerMouseState.AddRedZone
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.AddRedZone,
      ),
    );
  };

  const addRedZoneLineClickHandler = () => {
    dispatch(
      resetZonePlannerWithMouse(
        zonePlannerMouseState === ZoneControllerMouseState.AddRedZoneLine
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.AddRedZoneLine,
      ),
    );
  };

  const addRuleZoneClickHandler = () => {
    dispatch(
      resetZonePlannerWithMouse(
        zonePlannerMouseState === ZoneControllerMouseState.AddRuleZone
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.AddRuleZone,
      ),
    );
  };

  const undoActionClickHandler = () => {
    if (zonePlannerModeState === ZonePlannerModeState.EditMap) {
      if (editedMapData != null && editedMapData.undoActions.length > 0) {
        dispatch(setEditMapUpdateAction(editedMapData.undoActions[editedMapData.undoActions.length - 1]));
        onEditMapUndoRedo(true);
      }
    } else {
      dispatch(undoRedoLastAction(true));
    }
  };

  const redoActionClickHandler = () => {
    if (zonePlannerModeState === ZonePlannerModeState.EditMap) {
      if (editedMapData != null && editedMapData.redoActions.length > 0) {
        dispatch(setEditMapUpdateAction(editedMapData.redoActions[editedMapData.redoActions.length - 1]));
        onEditMapUndoRedo(false, true);
      }
    } else {
      dispatch(undoRedoLastAction(false));
    }
  };

  const handleChargingStationErrorSnackbarClose = () => {
    dispatch(patchStationDrag({ error: null, saveStatus: MoveStationSaveStatus.Idle }));
  };

  const handleChargingStationSaveSnackbarClose = () => {
    if (roomData != null && roomData.primaryStation != null) {
      dispatch(patchStationDrag({ error: null, saveStatus: MoveStationSaveStatus.Idle }));
    }
  };

  const plannerModeClickHandler = (mode: ZonePlannerModeState) => {
    if (mode != null) {
      dispatch(setSelectedLandmark(null));
      dispatch(setSelectedZone(null));
      dispatch(setZonePlannerMode(mode));
    }
  };

  const deleteIconClickHandler = () => {
    dispatch(
      resetZonePlannerWithMouse(
        zonePlannerMouseState === ZoneControllerMouseState.DeleteZone
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.DeleteZone,
      ),
    );
  };

  const deletePointIconClickHandler = () => {
    let newMouseState = ZoneControllerMouseState.DeletePoint;
    switch (zonePlannerMouseState) {
      case ZoneControllerMouseState.DeletePoint:
        newMouseState = ZoneControllerMouseState.Default;
        break;
      case ZoneControllerMouseState.DeleteRoomLimitationZonePoint:
        newMouseState = ZoneControllerMouseState.EditRoomLimitation;
        break;
      case ZoneControllerMouseState.EditRoomLimitation:
      case ZoneControllerMouseState.DragRoomLimitationZone:
        newMouseState = ZoneControllerMouseState.DeleteRoomLimitationZonePoint;
        break;
      default:
        break;
    }

    dispatch(setZonePlannerMouseState(newMouseState));
  };

  const dragIconClickHandler = () => {
    let newMouseState = ZoneControllerMouseState.DragZone;
    switch (zonePlannerMouseState) {
      case ZoneControllerMouseState.DragZone:
        newMouseState = ZoneControllerMouseState.Default;
        break;
      case ZoneControllerMouseState.DragRoomLimitationZone:
        newMouseState = ZoneControllerMouseState.EditRoomLimitation;
        break;
      case ZoneControllerMouseState.EditRoomLimitation:
      case ZoneControllerMouseState.DeleteRoomLimitationZonePoint:
        newMouseState = ZoneControllerMouseState.DragRoomLimitationZone;
        break;
      default:
        break;
    }

    dispatch(resetZonePlannerWithMouse(newMouseState));
  };

  const detailsIconClickHandler = () => {
    dispatch(
      setZonePlannerMouseState(
        zonePlannerMouseState === ZoneControllerMouseState.ShowDetails
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.ShowDetails,
      ),
    );
  };

  const copyIconClickHandler = () => {
    dispatch(
      resetZonePlannerWithMouse(
        zonePlannerMouseState === ZoneControllerMouseState.CopyZone
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.CopyZone,
      ),
    );
  };

  const addWayPointsClickHandler = () => {
    dispatch(
      resetZonePlannerWithMouse(
        zonePlannerMouseState === ZoneControllerMouseState.AddWayPoint
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.AddWayPoint,
      ),
    );
    if (!wayPointsData.isVisible) {
      dispatch(toggleWayPointVisibility());
    }
  };

  const addEvacuationPlacesClickHandler = () => {
    dispatch(
      resetZonePlannerWithMouse(
        zonePlannerMouseState === ZoneControllerMouseState.AddEvacuationPlace
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.AddEvacuationPlace,
      ),
    );
  };

  const cropMapClickHandler = () => {
    dispatch(
      resetZonePlannerWithMouse(
        zonePlannerMouseState === ZoneControllerMouseState.CropMap
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.CropMap,
      ),
    );
  };

  const editRoomLimitationClickHandler = () => {
    if (
      zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
      zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
      zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
    ) {
      dispatch(resetZonePlannerWithMouse(ZoneControllerMouseState.Default));
    } else {
      dispatch(resetZonePlannerWithMouse(ZoneControllerMouseState.EditRoomLimitation));
    }
  };

  const addGateClickHandler = () => {
    dispatch(
      resetZonePlannerWithMouse(
        zonePlannerMouseState === ZoneControllerMouseState.AddGate
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.AddGate,
      ),
    );
  };

  const addLandmarkClickHandler = () => {
    dispatch(
      resetZonePlannerWithMouse(
        zonePlannerMouseState === ZoneControllerMouseState.AddLandmarkPoint
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.AddLandmarkPoint,
      ),
    );
  };

  const sendToBackClickHandler = () => {
    dispatch(
      resetZonePlannerWithMouse(
        zonePlannerMouseState === ZoneControllerMouseState.SendToBack
          ? ZoneControllerMouseState.Default
          : ZoneControllerMouseState.SendToBack,
      ),
    );
  };

  const startZoneCleaningClickHandler = () => {
    if (selectedZoneInstance?.id != null) {
      const cleaningCommandMsg = getDefaultCleaningCommandButtonSender({
        navigatorName: NavigatorName.ZoneCleaning,
        selectedZoneIds: [selectedZoneInstance.id],
      });
      dispatch(sendRobotWebsocketMessage({ type: MessageType.Actions, message: cleaningCommandMsg }));
      if (isWebApp) {
        navigate(WebAppNavigationRoutes.routeDashboard);
      } else {
        setFilterParams((prevState) => {
          prevState.set(FilterKeys.Tab, SphereRobotTabs.Dashboard);
          return prevState;
        });
      }
    }
  };

  const showSelectedZoneOptions =
    zonePlannerModeState !== ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath &&
    zonePlannerMouseState !== ZoneControllerMouseState.DragZone &&
    selectedZoneInstance !== null &&
    selectedZoneInstance.points.length >= 2;

  const plannerModeOptions: {
    mode: ZonePlannerModeState;
    icon: React.ReactNode;
    disabled: boolean;
    disabledText?: string;
  }[] = [
    { mode: ZonePlannerModeState.Default, icon: <RoomIcon />, disabled: false },
    { mode: ZonePlannerModeState.MapRules, icon: <MapRulesIcon />, disabled: false },
  ];

  if (isAdvancedModeEnabled && isWebApp) {
    plannerModeOptions.push(
      {
        mode: ZonePlannerModeState.EditMap,
        icon: <EditMapIcon />,
        disabled: !isWebApp,
        disabledText: t("zone-planner-mode-description-EditMap-disabled"),
      },
      {
        mode: ZonePlannerModeState.EvacuationMode,
        icon: <EvacuationIcon />,
        disabled:
          getRobotApiConfig(ApiEndpointNames.EMERGENCY_EVACUATE, robotApiConfigs) == null ||
          getRobotApiConfig(ApiEndpointNames.EMERGENCY_EVACUATE, robotApiConfigs)?.apiProtocol === ApiProtocol.Disabled,
        disabledText: t("zone-planner-mode-description-EvacuationMode-disabled"),
      },
    );
  }

  return (
    <React.Fragment>
      {zonePlannerModeState !== ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath &&
        !inMappingWizard &&
        (isWebApp || (rswSoftwareVersion && !isVersionAGreaterThanB("2026.1.0", rswSoftwareVersion))) && (
          <Card className={classes.topControlsContainer} sx={{ direction: "row", alignItems: "center" }}>
            {plannerModeOptions.map(
              (o) => (
                <Tooltip
                  key={o.mode}
                  title={o.disabled ? o.disabledText : matchesSmDown ? t(`zone-planner-mode-${o.mode}`) : undefined}
                >
                  <span>
                    <Button
                      disabled={o.disabled}
                      onClick={() => plannerModeClickHandler(o.mode)}
                      size="large"
                      sx={(theme) => ({
                        color:
                          zonePlannerModeState === o.mode
                            ? theme.palette.primary.main
                            : o.disabled
                              ? theme.palette.action.disabled
                              : theme.palette.action.active,
                        borderRadius: 0,
                        px: 2,
                        py: 1.5,
                      })}
                    >
                      <Stack direction="row" spacing={1}>
                        {o.icon}
                        {!matchesSmDown && <Typography variant="button">{t(`zone-planner-mode-${o.mode}`)}</Typography>}
                      </Stack>
                    </Button>
                  </span>
                </Tooltip>
              ) /*  */,
            )}
          </Card>
        )}
      <Card className={classes.mapControlsContainer}>
        <CustomTabs
          orientation="vertical"
          key={zonePlannerModeState}
          value={false}
          id="zone-planner-map-edit-tools"
          TabIndicatorProps={{
            style: { transition: "none", display: "none" },
          }}
        >
          {!isTransitionRoom && (
            <SideBarIconButton
              hide={zonePlannerModeState !== ZonePlannerModeState.Default}
              isSelected={zonePlannerMouseState === ZoneControllerMouseState.AddCleaningZone}
              tooltipText={t("zoneplannercontrols-add-cleaning-zone")}
              onClick={addCleaningZoneClickHandler}
              disabled={
                zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
                zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
                zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
                zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
              }
            >
              <Box
                className={cx(classes.cleaningZoneIcon, classes.zoneSquareIcon, {
                  [classes.disabledZoneIcon]:
                    zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
                    chargingStationDragData.isSelected ||
                    isReadOnly,
                })}
              />
            </SideBarIconButton>
          )}
          {!isTransitionRoom && (
            <SideBarIconButton
              hide={zonePlannerModeState !== ZonePlannerModeState.Default}
              isSelected={zonePlannerMouseState === ZoneControllerMouseState.AddTeachAndRepeatZone}
              tooltipText={t("zoneplannercontrols-add-teachandrepeat-zone")}
              onClick={addTeachAndRepeatZoneClickHandler}
              disabled={
                !isRobotConnected ||
                zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
                zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
                zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
                zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
              }
            >
              <Box
                className={cx(classes.teachAndRepeatZoneIcon, classes.zoneSquareIcon, {
                  [classes.disabledZoneIcon]:
                    zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
                    chargingStationDragData.isSelected ||
                    isReadOnly,
                })}
              />
            </SideBarIconButton>
          )}
          <SideBarIconButton
            hide={
              zonePlannerModeState === ZonePlannerModeState.Default ||
              zonePlannerModeState === ZonePlannerModeState.EditMap ||
              zonePlannerModeState === ZonePlannerModeState.EvacuationMode ||
              zonePlannerModeState === ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath
            }
            isSelected={zonePlannerMouseState === ZoneControllerMouseState.AddRuleZone}
            tooltipText={t("zoneplannercontrols-add-rule-zone")}
            onClick={addRuleZoneClickHandler}
            disabled={
              zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
              zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
              zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
              zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
            }
          >
            <Box
              className={cx(classes.ruleZoneIcon, classes.zoneSquareIcon, {
                [classes.disabledZoneIcon]:
                  zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
                  chargingStationDragData.isSelected ||
                  isReadOnly,
              })}
            />
          </SideBarIconButton>
          <SideBarIconButton
            hide={
              zonePlannerModeState === ZonePlannerModeState.EditMap ||
              zonePlannerModeState === ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath
            }
            isSelected={zonePlannerMouseState === ZoneControllerMouseState.AddRedZone}
            tooltipText={
              zonePlannerModeState === ZonePlannerModeState.EvacuationMode
                ? t("zoneplannercontrols-add-evacuation-red-zone")
                : t("zoneplannercontrols-add-red-zone")
            }
            onClick={addRedZoneClickHandler}
            disabled={
              zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
              zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
              zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
              zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
            }
          >
            <Box
              className={cx(classes.redZoneIcon, classes.zoneSquareIcon, {
                [classes.disabledZoneIcon]:
                  zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
                  chargingStationDragData.isSelected ||
                  isReadOnly,
              })}
            />
          </SideBarIconButton>
          <SideBarIconButton
            hide={
              zonePlannerModeState === ZonePlannerModeState.EditMap ||
              zonePlannerModeState === ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath
            }
            isSelected={zonePlannerMouseState === ZoneControllerMouseState.AddRedZoneLine}
            tooltipText={
              zonePlannerModeState === ZonePlannerModeState.EvacuationMode
                ? t("zoneplannercontrols-add-evacuation-red-zone-line")
                : t("zoneplannercontrols-add-red-zone-line")
            }
            onClick={addRedZoneLineClickHandler}
            disabled={
              zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
              zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
              zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
              zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
            }
          >
            <div className={cx(classes.redZoneLineIconContainer)}>
              <Box
                className={cx(classes.redZoneLineIcon, {
                  [classes.disabledZoneIcon]:
                    zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
                    chargingStationDragData.isSelected ||
                    isReadOnly,
                })}
              />
            </div>
          </SideBarIconButton>
          {!isTransitionRoom && (
            <SideBarIconButton
              hide={zonePlannerModeState !== ZonePlannerModeState.Default}
              isSelected={zonePlannerMouseState === ZoneControllerMouseState.AddLandmarkPoint}
              tooltipText={t("zoneplannercontrols-add-landmark-point")}
              onClick={addLandmarkClickHandler}
              disabled={
                zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
                zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
                zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
                zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
              }
            >
              <LandmarkIcon
                sx={(theme) => ({
                  ...(zonePlannerMouseState !== ZoneControllerMouseState.CropMap &&
                    !chargingStationDragData.isSelected &&
                    !isReadOnly && { color: LANDMARK_COLOR }),
                })}
              />
            </SideBarIconButton>
          )}
          <SideBarIconButton
            hide={
              zonePlannerModeState !== ZonePlannerModeState.MapRules ||
              !isAdvancedModeEnabled ||
              getRobotApiConfig(ApiEndpointNames.GATES, robotApiConfigs) == null ||
              getRobotApiConfig(ApiEndpointNames.GATES, robotApiConfigs)?.apiProtocol === ApiProtocol.Disabled
            }
            isSelected={zonePlannerMouseState === ZoneControllerMouseState.AddGate}
            tooltipText={t("zoneplannercontrols-add-gate")}
            onClick={addGateClickHandler}
            disabled={
              zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
              zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
              zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
              zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
            }
          >
            <div className={classes.redZoneLineIconContainer}>
              <Box
                className={cx(classes.gateIcon, {
                  [classes.gateDisabled]: zonePlannerMouseState === ZoneControllerMouseState.CropMap,
                })}
              />
            </div>
          </SideBarIconButton>
          <SideBarIconButton
            hide={
              zonePlannerModeState !== ZonePlannerModeState.Default &&
              zonePlannerModeState !== ZonePlannerModeState.MapRules &&
              zonePlannerModeState !== ZonePlannerModeState.EvacuationMode
            }
            tooltipText={t("zoneplannercontrols-add-waypoints")}
            onClick={addWayPointsClickHandler}
            isSelected={zonePlannerMouseState === ZoneControllerMouseState.AddWayPoint}
            disabled={
              chargingStationDragData.isSelected ||
              zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
              zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
              zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
              zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
            }
          >
            <WaypointIcon
              sx={{
                ...(zonePlannerMouseState !== ZoneControllerMouseState.CropMap &&
                  !chargingStationDragData.isSelected &&
                  !isReadOnly && { color: WAYPOINT_COLOR }),
              }}
            />
          </SideBarIconButton>
          <SideBarIconButton
            hide={zonePlannerModeState !== ZonePlannerModeState.EvacuationMode}
            tooltipText={t("zoneplannercontrols-add-evacuation-place")}
            onClick={addEvacuationPlacesClickHandler}
            isSelected={zonePlannerMouseState === ZoneControllerMouseState.AddEvacuationPlace}
          >
            <LandmarkIcon
              sx={{
                ...(!isReadOnly && { color: EVACUATION_PLACE_COLOR }),
              }}
            />
          </SideBarIconButton>
          <SideBarIconButton
            hide={
              zonePlannerModeState === ZonePlannerModeState.Default ||
              zonePlannerModeState === ZonePlannerModeState.MapRules ||
              zonePlannerModeState === ZonePlannerModeState.EvacuationMode ||
              zonePlannerModeState === ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath ||
              !isAdvancedModeEnabled ||
              !isWebApp
            }
            tooltipText={t("zoneplannercontrols-crop-map")}
            onClick={cropMapClickHandler}
            isSelected={zonePlannerMouseState === ZoneControllerMouseState.CropMap}
            disabled={
              chargingStationDragData.isSelected ||
              zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
              zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
              zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
            }
          >
            <CropIcon />
          </SideBarIconButton>
          <SideBarIconButton
            hide={
              zonePlannerModeState === ZonePlannerModeState.Default ||
              zonePlannerModeState === ZonePlannerModeState.MapRules ||
              zonePlannerModeState === ZonePlannerModeState.EvacuationMode ||
              zonePlannerModeState === ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath ||
              !isAdvancedModeEnabled ||
              limitZone.filter((z) => z.room === roomData?.id).length === 0
            }
            tooltipText={t("zoneplannercontrols-limit-room")}
            onClick={editRoomLimitationClickHandler}
            isSelected={
              zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
              zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
              zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
            }
            disabled={chargingStationDragData.isSelected}
          >
            <LimitZoneIcon />
          </SideBarIconButton>
          <DividerElement />

          <SideBarIconButton
            hide={
              zonePlannerModeState === ZonePlannerModeState.EditMap ||
              zonePlannerModeState === ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath
            }
            isSelected={layersList.isVisible}
            tooltipText={t("zoneplannercontrols-view-layers")}
            onClick={layersList.toggleVisible}
            disabled={
              chargingStationDragData.isSelected ||
              zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
              zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
              zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
            }
            isPinProtected={false}
          >
            <LayersListIcon />
          </SideBarIconButton>
          <SideBarIconButton
            hide={zonePlannerModeState === ZonePlannerModeState.EditMap}
            isSelected={gridOverlay.isVisible}
            tooltipText={t("zoneplannercontrols-grid-overlay")}
            onClick={gridOverlay.toggleVisible}
            disabled={zonePlannerMouseState === ZoneControllerMouseState.CropMap || chargingStationDragData.isSelected}
            isPinProtected={false}
          >
            <MapGridIcon />
          </SideBarIconButton>
          <SideBarIconButton
            key="undoLastChangeButton"
            tooltipText={t("action-undo-last-change")}
            onClick={undoActionClickHandler}
            disabled={
              (zonePlannerModeState !== ZonePlannerModeState.EditMap &&
                (mapHistory.undo.length === 0 ||
                  zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
                  chargingStationDragData.isSelected)) ||
              (zonePlannerModeState === ZonePlannerModeState.EditMap && (editedMapData?.undoActions ?? []).length === 0)
            }
          >
            <UndoIcon />
          </SideBarIconButton>
          <SideBarIconButton
            key="redoLastChangeButton"
            tooltipText={t("action-redo-last-change")}
            onClick={redoActionClickHandler}
            disabled={
              (zonePlannerModeState !== ZonePlannerModeState.EditMap &&
                (mapHistory.redo.length === 0 ||
                  zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
                  chargingStationDragData.isSelected)) ||
              (zonePlannerModeState === ZonePlannerModeState.EditMap && (editedMapData?.redoActions ?? []).length === 0)
            }
          >
            <RedoIcon />
          </SideBarIconButton>
          <DividerElement />
          <SideBarIconButton
            hide={
              zonePlannerModeState === ZonePlannerModeState.EditMap ||
              zonePlannerModeState === ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath
            }
            isSelected={zonePlannerMouseState === ZoneControllerMouseState.DeleteZone}
            tooltipText={
              zonePlannerModeState === ZonePlannerModeState.EvacuationMode
                ? t("zoneplannercontrols-delete-zones-evacuation-places")
                : t("zoneplannercontrols-delete-zones")
            }
            onClick={deleteIconClickHandler}
            color={zonePlannerMouseState === ZoneControllerMouseState.DeleteZone ? "secondary" : "default"}
            disabled={
              chargingStationDragData.isSelected ||
              zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
              zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
              zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
              zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
            }
          >
            <DeleteIcon />
          </SideBarIconButton>
          <SideBarIconButton
            hide={
              zonePlannerModeState === ZonePlannerModeState.EditMap &&
              zonePlannerMouseState !== ZoneControllerMouseState.EditRoomLimitation &&
              zonePlannerMouseState !== ZoneControllerMouseState.DragRoomLimitationZone &&
              zonePlannerMouseState !== ZoneControllerMouseState.DeleteRoomLimitationZonePoint
            }
            isSelected={
              zonePlannerMouseState === ZoneControllerMouseState.DragZone ||
              zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone
            }
            tooltipText={
              zonePlannerModeState === ZonePlannerModeState.EvacuationMode
                ? t("zoneplannercontrols-drag-zones-evacuation-places")
                : t("zoneplannercontrols-drag-zones")
            }
            onClick={dragIconClickHandler}
            color={
              zonePlannerMouseState === ZoneControllerMouseState.DragZone ||
              zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone
                ? "primary"
                : "default"
            }
            disabled={zonePlannerMouseState === ZoneControllerMouseState.CropMap || chargingStationDragData.isSelected}
          >
            <DragIcon />
          </SideBarIconButton>
          <SideBarIconButton
            hide={
              zonePlannerModeState === ZonePlannerModeState.EditMap ||
              zonePlannerModeState === ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath ||
              zonePlannerModeState === ZonePlannerModeState.MapRules
            }
            isSelected={zonePlannerMouseState === ZoneControllerMouseState.CopyZone}
            tooltipText={t("zoneplannercontrols-copy-zones")}
            onClick={copyIconClickHandler}
            color={zonePlannerMouseState === ZoneControllerMouseState.CopyZone ? "primary" : "default"}
            disabled={
              chargingStationDragData.isSelected ||
              zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
              zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
              zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
              zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
            }
          >
            <CopyIcon />
          </SideBarIconButton>
          <SideBarIconButton
            hide={
              zonePlannerModeState === ZonePlannerModeState.EditMap ||
              zonePlannerModeState === ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath
            }
            isSelected={zonePlannerMouseState === ZoneControllerMouseState.SendToBack}
            tooltipText={t("zoneplannercontrols-send-zone-to-back")}
            onClick={sendToBackClickHandler}
            color={zonePlannerMouseState === ZoneControllerMouseState.SendToBack ? "primary" : "default"}
            disabled={
              chargingStationDragData.isSelected ||
              zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
              zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
              zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
              zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
            }
          >
            <SendBelowIcon />
          </SideBarIconButton>
          {(showSelectedZoneOptions || gatesData.selectedId != null) && <DividerElement />}
          {(showSelectedZoneOptions || gatesData.selectedId != null) && (
            <SideBarIconButton
              isSelected={zonePlannerMouseState === ZoneControllerMouseState.ShowDetails}
              tooltipText={
                gatesData.selectedId != null
                  ? t("zoneplannercontrols-view-gate-details")
                  : t("zoneplannercontrols-view-zone-details")
              }
              onClick={detailsIconClickHandler}
              disabled={
                zonePlannerMouseState === ZoneControllerMouseState.CropMap || chargingStationDragData.isSelected
              }
              isPinProtected={false}
            >
              <SettingsIcon />
            </SideBarIconButton>
          )}
          {(showSelectedZoneOptions ||
            zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
            zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
            zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint ||
            (selectedZoneInstance != null &&
              zonePlannerModeState === ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath)) && (
            <SideBarIconButton
              isSelected={
                zonePlannerMouseState === ZoneControllerMouseState.DeletePoint ||
                zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
              }
              tooltipText={t("zoneplannercontrols-delete-points")}
              onClick={deletePointIconClickHandler}
              color={
                zonePlannerMouseState === ZoneControllerMouseState.DeletePoint ||
                zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint
                  ? "secondary"
                  : "default"
              }
              disabled={
                zonePlannerMouseState === ZoneControllerMouseState.CropMap || chargingStationDragData.isSelected
              }
            >
              <DeletePointIcon />
            </SideBarIconButton>
          )}
          {showSelectedZoneOptions &&
            (isCleaningZone(selectedZoneInstance) || isTeachAndRepeatZone(selectedZoneInstance)) && (
              <SideBarIconButton
                tooltipText={
                  isCleaningActionEnabled
                    ? t("zoneplannercontrols-start-cleaning")
                    : t("zoneplannercontrols-start-cleaning-robot-not-ready")
                }
                onClick={startZoneCleaningClickHandler}
                disabled={!isCleaningActionEnabled || zonePlannerMouseState === ZoneControllerMouseState.CropMap}
              >
                <img
                  src={ZoneCleaningNavigatorIcon}
                  alt={t("navigator-ZoneCleaning")}
                  className={cx(classes.navigatorIcon, {
                    [classes.navigatorIconDisabled]: !isCleaningActionEnabled || isReadOnly,
                  })}
                />
              </SideBarIconButton>
            )}
          {(zonePlannerMouseState === ZoneControllerMouseState.EditRoomLimitation ||
            zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
            zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint) && <DividerElement />}
          <EditMapTools />
        </CustomTabs>
      </Card>

      <CustomSnackbar
        title={t("error-saving-room")}
        open={chargingStationDragData.saveStatus === MoveStationSaveStatus.Failed}
        onClose={handleChargingStationErrorSnackbarClose}
        textMessages={chargingStationDragData.error != null ? [chargingStationDragData.error] : undefined}
        severity="error"
      />
      <CustomSnackbar
        title={t("success-saving-room")}
        severity="success"
        textMessages={[t("info-return-robot-to-moved-station")]}
        open={chargingStationDragData.saveStatus === MoveStationSaveStatus.Saved}
        onClose={handleChargingStationSaveSnackbarClose}
      />
      <CustomSnackbar
        open={mapPutStatus?.status === HttpFetchStatus.Success}
        title={t("success-saving-room")}
        onClose={resetMapPutStatus}
      />
    </React.Fragment>
  );
};

export default ZonePlannerSideBar;
